'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardBody, CardHeader, Button, Input, Select, SelectItem, Switch, Divider, Chip, Alert } from '@heroui/react';
import { useConnection } from 'wagmi';
import { Address, formatUnits, parseUnits } from 'viem';
import { AnimatePresence, motion } from "motion/react";
import { usePathname } from 'next/navigation';
import { ChainType, chainSettings, defaultNativeAddress } from '@/constants/chain-specific';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';
import type { RequiredAggregatorChainType } from '@/constants/project-specific';
import { SHIB_TOKENS, DOGE_TOKENS } from '@/constants/project-specific/tokens';
import type { TokenConfigType, TokenDataAllType } from '@/constants/types/currency';
import { useLiquidityManagement, AddLiquidityParams } from '@/hooks/liquidity/useLiquidityManagement';
import { useLiquidityCalculations } from '@/hooks/liquidity/useLiquidityManagement';
import { usePoolInfo } from '@/hooks/liquidity/usePoolInfo';
import { useNativeTokenHandler } from '@/hooks/liquidity/useNativeTokenHandler';
import { useExistingPosition } from '@/hooks/liquidity/useExistingPosition';
import type { LiquidityPosition } from '@/hooks/liquidity/useLiquidityPositions';
import { formatNumber } from '@/util/balance/formatNumber';
import TokenSwapInput from '@/components/Input/SwapInput';
import TokenSelect from '@/components/Tokens/TokenSelect';
import { ExistingPositionSection } from './ExistingPositionSection';
import { RemoveLiquidityModal } from './RemoveLiquidityModal';
import { useBalance } from 'wagmi';
import { useDisclosure } from '@heroui/react';
import { useAtom } from 'jotai';
import { swapAggConfigAtom } from '@/config/atoms/swapAggConfigAtom';
import { AnimateNumber } from 'motion-plus/react';
import { useSharedTokenBalances } from '@/hooks/tokens/useSharedTokenBalances';
import { useTokenPrices } from '@/hooks/liquidity/useTokenPrices';
import { computePairAddress } from '@/lib/liquidity/compute-pair-address';
import type { NativeTokenPrices } from '@/constants/project-specific/farms/types';
import { getNativePrices } from '@/lib/price-data/geckoPrices';
import { useQuery } from '@tanstack/react-query';
import type { Hex } from 'viem';
interface AddLiquidityFormProps {
  dexSlug: string;
  chainId?: ChainType;
  tokenData?: Record<Address, TokenDataAllType>;
  positions?: LiquidityPosition[];
  /** Initial position to pre-select tokens from (e.g., from URL pair address) */
  initialPosition?: LiquidityPosition;
  onSuccess?: () => void;
  className?: string;
  refetchPosition?: (pairAddress: string, chainId: number, dexSlug: string) => Promise<void>;
}
interface PriceRange {
  lower: number;
  upper: number;
  current: number;
}
export function AddLiquidityForm({
  dexSlug,
  chainId,
  tokenData,
  positions = [],
  initialPosition,
  onSuccess,
  className,
  refetchPosition
}: AddLiquidityFormProps) {
  const {
    address,
    chainId: connectedChainId
  } = useConnection();
  const {
    addLiquidity,
    state
  } = useLiquidityManagement(connectedChainId);
  const {
    calculateV3Position,
    calculateV2Position
  } = useLiquidityCalculations();

  // Get available tokens based on chain (as array for searching)
  const availableTokens = useMemo(() => {
    const tokenIndex = connectedChainId === 2000 ? DOGE_TOKENS : SHIB_TOKENS;
    return Object.values(tokenIndex) as TokenConfigType[];
  }, [connectedChainId]);

  // Helper function to find or create token from position token info
  const resolveTokenFromPosition = useCallback((positionToken: {
    address: Address;
    symbol: string;
    name: string;
    decimals: number;
    logo?: string;
  } | undefined): TokenConfigType | undefined => {
    if (!positionToken) return undefined;

    // First try to find by address in our token list
    const foundToken = availableTokens.find((t: TokenConfigType) => t.address.toLowerCase() === positionToken.address.toLowerCase());
    if (foundToken) {
      console.log('[AddLiquidityForm] Token found in list:', foundToken.symbol);
      return foundToken;
    }

    // If not in list, create a token object from contract data
    console.log('[AddLiquidityForm] Token not in list, creating from contract data:', positionToken.symbol);
    return {
      name: positionToken.name,
      symbol: positionToken.symbol,
      address: positionToken.address,
      decimals: positionToken.decimals,
      logo: positionToken.logo || `/images/tokens/${positionToken.symbol.toLowerCase()}.png`
    };
  }, [availableTokens]);

  // Form state
  const [token0, setToken0] = useState<TokenConfigType | undefined>();
  const [token1, setToken1] = useState<TokenConfigType | undefined>();
  const [amount0, setAmount0] = useState<string>('');
  const [amount1, setAmount1] = useState<string>('');
  const [version, setVersion] = useState<'v2' | 'v3'>('v3');

  // Pre-populate tokens from initialPosition (e.g., from URL pair address)
  useEffect(() => {
    if (initialPosition) {
      console.log('[AddLiquidityForm] Pre-populating from initialPosition:', {
        token0: initialPosition.token0?.symbol,
        token1: initialPosition.token1?.symbol
      });
      const resolvedToken0 = resolveTokenFromPosition(initialPosition.token0);
      const resolvedToken1 = resolveTokenFromPosition(initialPosition.token1);
      if (resolvedToken0) {
        setToken0(resolvedToken0);
      }
      if (resolvedToken1) {
        setToken1(resolvedToken1);
      }
    }
  }, [initialPosition, resolveTokenFromPosition]);
  const {
    poolInfo,
    optimalAmounts,
    updateAmount,
    userInput,
    isLoading: poolLoading
  } = usePoolInfo(token0, token1, dexSlug, connectedChainId);
  const {
    needsWrapping,
    getDisplayInfo,
    dexRequiresWrapped,
    getEffectiveToken
  } = useNativeTokenHandler(connectedChainId as ChainType);

  // Check for existing position with same token pair
  const existingPosition = useExistingPosition(token0, token1, dexSlug, positions, connectedChainId);

  // Update amounts when optimal amounts change (for V2 ratio sync)
  useEffect(() => {
    if (version === 'v2' && poolInfo?.exists && optimalAmounts.isOptimal) {
      if (userInput.field === 'token0') {
        setAmount1(optimalAmounts.amount1);
      } else if (userInput.field === 'token1') {
        setAmount0(optimalAmounts.amount0);
      }
    }
  }, [version, poolInfo?.exists, optimalAmounts, userInput.field]);

  // Update browser history when a pool is successfully loaded (for shareable URLs)
  // Only include pair address in URL when pool actually exists
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (poolInfo?.exists && poolInfo?.pairAddress) {
      // Pool exists - use the actual pair address
      const targetUrl = `/liquidity/${poolInfo.pairAddress}/add`;
      if (window.location.pathname !== targetUrl) {
        window.history.pushState(null, '', targetUrl);
      }
    } else if (token0?.address && token1?.address) {
      // Pool doesn't exist - use base add route without address
      const targetUrl_0 = `/liquidity/add`;
      if (window.location.pathname !== targetUrl_0 && window.location.pathname.includes('/liquidity/')) {
        window.history.pushState(null, '', targetUrl_0);
      }
    }
  }, [poolInfo?.exists, poolInfo?.pairAddress, token0?.address, token1?.address]);
  const [priceRange, setPriceRange] = useState<PriceRange>({
    lower: 0,
    upper: 0,
    current: 0
  });
  const [fullRange, setFullRange] = useState(false);

  // Use global slippage from swap config
  const [aggConfig] = useAtom(swapAggConfigAtom);

  // Token selection modals
  const {
    isOpen: isToken0SelectOpen,
    onOpen: onToken0SelectOpen,
    onOpenChange: onToken0SelectOpenChange
  } = useDisclosure();
  const {
    isOpen: isToken1SelectOpen,
    onOpen: onToken1SelectOpen,
    onOpenChange: onToken1SelectOpenChange
  } = useDisclosure();

  // Remove liquidity modal for existing position
  const {
    isOpen: isRemoveModalOpen,
    onOpen: onRemoveModalOpen,
    onOpenChange: onRemoveModalOpenChange
  } = useDisclosure();
  const [positionToRemove, setPositionToRemove] = useState<LiquidityPosition | null>(null);

  // Handler for opening remove modal
  const handleRemoveClick = (position: LiquidityPosition) => {
    setPositionToRemove(position);
    onRemoveModalOpen();
  };

  // Get DEX info
  const dexInfo = useMemo(() => DEX_LIST[dexSlug], [dexSlug]);

  // Tokens to fetch balances for (all available tokens except native)
  const tokensToFetch = useMemo(() => {
    return availableTokens.filter((token: TokenConfigType) => token.address !== defaultNativeAddress);
  }, [availableTokens]);

  // Fetch balances for all available tokens
  const {
    tokenData: fetchedTokenData
  } = useSharedTokenBalances(tokensToFetch, address, dexInfo?.router?.address as Address, connectedChainId, {
    enabled: !!address
  });

  // Merge fetched data with passed tokenData prop
  const mergedTokenData = useMemo(() => {
    return {
      ...tokenData,
      ...fetchedTokenData
    };
  }, [tokenData, fetchedTokenData]);

  // Fetch token prices for selected tokens
  const tokenAddressesForPrices = useMemo(() => {
    const addresses: Address[] = [];
    if (token0?.address && token0.address !== defaultNativeAddress) {
      addresses.push(token0.address as Address);
    }
    if (token1?.address && token1.address !== defaultNativeAddress) {
      addresses.push(token1.address as Address);
    }
    return addresses;
  }, [token0, token1]);
  const {
    prices: tokenPricesData
  } = useTokenPrices(tokenAddressesForPrices, connectedChainId);

  // Convert token prices to simple Record<string, number> format for TokenSwapInput
  const tokenPrices = useMemo(() => {
    const priceMap: Record<string, number> = {};
    for (const [addr, priceInfo] of Object.entries(tokenPricesData)) {
      priceMap[addr.toLowerCase()] = priceInfo.priceUsd;
    }
    return priceMap;
  }, [tokenPricesData]);

  // Fetch native token prices
  const {
    data: nativePrices
  } = useQuery({
    queryKey: ['nativePrices', connectedChainId],
    queryFn: () => getNativePrices([connectedChainId as ChainType]),
    enabled: !!connectedChainId,
    staleTime: 60000
  });

  // Get native currency balance (not included in tokenData)
  const {
    data: nativeBalance
  } = useBalance({
    address,
    chainId: connectedChainId
  });

  // Helper function to get token balance
  const getTokenBalance = (token_0?: TokenConfigType): number => {
    if (!token_0 || !address) return 0;

    // For native currency, use native balance
    if (token_0.address === defaultNativeAddress) {
      return nativeBalance ? parseFloat(formatUnits(nativeBalance.value, nativeBalance.decimals)) : 0;
    }

    // For ERC20 tokens, use merged token data (fetched + passed)
    const tokenBalance = mergedTokenData?.[token_0.address as Address];
    return tokenBalance ? tokenBalance.balance?.formatted ?? 0 : 0;
  };

  // Determine available versions
  const availableVersions = useMemo(() => {
    const versions: ('v2' | 'v3')[] = [];
    // Velodrome-type DEXes use V2-style liquidity pools
    if (dexInfo?.type === 'v2' || dexInfo?.type === 'velodrome' || !dexInfo?.type) versions.push('v2');
    if (dexInfo?.type === 'v3') versions.push('v3');
    return versions;
  }, [dexInfo]);

  // Compute expected pair address using CREATE2 (for new pools)
  const computedPairAddress = useMemo(() => {
    if (!dexInfo?.factory?.address || !dexInfo?.factory?.codeHash) return null;

    // Get effective token addresses (wrapped if needed)
    const effectiveToken0 = getEffectiveToken(token0, dexSlug);
    const effectiveToken1 = getEffectiveToken(token1, dexSlug);
    if (!effectiveToken0?.address || !effectiveToken1?.address) return null;
    try {
      return computePairAddress(dexInfo.factory.address, effectiveToken0.address, effectiveToken1.address, dexInfo.factory.codeHash as Hex);
    } catch (e) {
      console.debug('[AddLiquidityForm] Could not compute CREATE2 pair address:', e);
      return null;
    }
  }, [dexInfo, token0, token1, dexSlug, getEffectiveToken]);

  // Set default version
  useEffect(() => {
    if (availableVersions.length > 0 && !availableVersions.includes(version)) {
      setVersion(availableVersions[0]);
    }
  }, [availableVersions, version]);

  // Calculate position details
  const positionCalculation = useMemo(() => {
    if (!token0 || !token1 || !amount0 || !amount1) return null;
    if (version === 'v3') {
      // Calculate V3 position with price ranges
      const tickLower = fullRange ? 887220 : Math.floor(Math.log(priceRange.lower) / Math.log(1.0001));
      const tickUpper = fullRange ? 887220 : Math.floor(Math.log(priceRange.upper) / Math.log(1.0001));
      const currentTick = Math.floor(Math.log(priceRange.current) / Math.log(1.0001));
      return calculateV3Position(token0, token1, amount0, amount1, tickLower, tickUpper, currentTick);
    } else {
      // Calculate V2 position
      return calculateV2Position(token0, token1, amount0, amount1, '1000000',
      // Mock reserves
      '1000000');
    }
  }, [token0, token1, amount0, amount1, version, priceRange, fullRange, calculateV3Position, calculateV2Position]);
  const handleSubmit = async () => {
    if (!token0 || !token1) return;

    // Use optimal amounts for V2 if pool exists, otherwise use user input
    const finalAmount0 = version === 'v2' && poolInfo?.exists ? optimalAmounts.amount0 : amount0;
    const finalAmount1 = version === 'v2' && poolInfo?.exists ? optimalAmounts.amount1 : amount1;
    if (!finalAmount0 || !finalAmount1) return;
    const params: AddLiquidityParams = {
      token0,
      token1,
      amount0: finalAmount0,
      amount1: finalAmount1,
      dexSlug,
      version,
      slippage: aggConfig.slippageTolerance,
      deadline: Math.floor(Date.now() / 1000) + 1200,
      // 20 minutes
      priceRange: version === 'v3' ? priceRange : undefined
    };
    if (version === 'v3') {
      if (fullRange) {
        params.tickLower = -887220; // Min tick for full range
        params.tickUpper = 887220; // Max tick for full range
      } else {
        // Convert price to tick using the standard formula: tick = log(price) / log(1.0001)
        // Ensure ticks are valid and properly spaced
        const tickSpacing = 60; // Standard tick spacing for 0.3% fee tier
        params.tickLower = Math.floor(Math.log(priceRange.lower) / Math.log(1.0001) / tickSpacing) * tickSpacing;
        params.tickUpper = Math.ceil(Math.log(priceRange.upper) / Math.log(1.0001) / tickSpacing) * tickSpacing;

        // Ensure valid tick range
        params.tickLower = Math.max(params.tickLower, -887220);
        params.tickUpper = Math.min(params.tickUpper, 887220);
      }
      console.log(`🎯 V3 Price Range - Lower: ${priceRange.lower} (tick: ${params.tickLower}), Upper: ${priceRange.upper} (tick: ${params.tickUpper})`);
    }
    try {
      await addLiquidity(params);

      // If we have refetchPosition and pool info with pair address, use targeted refresh
      if (refetchPosition && poolInfo?.pairAddress && connectedChainId) {
        console.log('[AddLiquidityForm] Refetching position after add:', poolInfo.pairAddress);
        await refetchPosition(poolInfo.pairAddress, connectedChainId, dexSlug);
      }
      onSuccess?.();
    } catch (error) {
      console.error('Add liquidity failed:', error);
    }
  };
  const isFormValid = useMemo(() => {
    if (!token0 || !token1 || !dexSlug) return false;

    // Use optimal amounts for V2 if pool exists, otherwise use user input
    const finalAmount0_0 = version === 'v2' && poolInfo?.exists ? optimalAmounts.amount0 : amount0;
    const finalAmount1_0 = version === 'v2' && poolInfo?.exists ? optimalAmounts.amount1 : amount1;
    return parseFloat(finalAmount0_0 || '0') > 0 && parseFloat(finalAmount1_0 || '0') > 0;
  }, [token0, token1, dexSlug, version, poolInfo?.exists, optimalAmounts.amount0, optimalAmounts.amount1, amount0, amount1]);
  return <div className={`space-y-6 ${className}`}>


      {/* Version Selection */}
      {availableVersions.length > 1 && <Card>
          <CardBody>
            <div className="space-y-3">
              <label className="text-sm font-medium">Protocol Version</label>
              <Select selectedKeys={[version]} onSelectionChange={keys => setVersion(Array.from(keys)[0] as 'v2' | 'v3')}>
                {availableVersions.map(v => <SelectItem key={v} textValue={v.toUpperCase()}>
                    <div className="flex items-center justify-between w-full">
                      <span className="font-medium">{v.toUpperCase()}</span>
                      <span className="text-xs text-default-500">
                        {v === 'v3' ? 'Concentrated Liquidity' : 'Full Range'}
                      </span>
                    </div>
                  </SelectItem>)}
              </Select>
            </div>
          </CardBody>
        </Card>}

      {/* Token Selection */}
      <Card>
        <CardHeader>
          <h4 className="text-medium font-semibold">Select Token Pair</h4>
        </CardHeader>
        <CardBody className="space-y-3">
          <motion.div key={token0?.address || 'no-token-0'} initial={{
          opacity: 0.8,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} style={{
          willChange: 'opacity, transform'
        }}>
            <TokenSwapInput label="Token A" selectedToken={token0} onSelectClick={onToken0SelectOpen} value={poolInfo?.exists ? userInput.field === 'token0' ? userInput.value : optimalAmounts.amount0 : amount0} onChange={e_0 => {
            const value = e_0.target.value;
            setAmount0(value);
            updateAmount('token0', value);
          }} balance={getTokenBalance(token0)} placeholder="0.0" chainId={connectedChainId as any} nativePrices={nativePrices} tokenPrices={tokenPrices} />
          </motion.div>

          <div className="flex justify-center absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10">
            <motion.div whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.9
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} style={{
            willChange: 'transform'
          }} className="flex justify-center absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10">
              <Button isIconOnly className="absolute text-xl" variant="shadow" size="md" radius="full" onPress={() => {
              // Swap token positions
              const temp = token0;
              setToken0(token1);
              setToken1(temp);
              const tempAmount = amount0;
              setAmount0(amount1);
              setAmount1(tempAmount);
            }}>
                +
              </Button>
            </motion.div>
          </div>

          <motion.div key={token1?.address || 'no-token-1'} initial={{
          opacity: 0.8,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} style={{
          willChange: 'opacity, transform'
        }}>
            <TokenSwapInput label="Token B" selectedToken={token1} onSelectClick={onToken1SelectOpen} value={poolInfo?.exists ? userInput.field === 'token1' ? userInput.value : optimalAmounts.amount1 : amount1} onChange={e_1 => {
            const value_0 = e_1.target.value;
            setAmount1(value_0);
            updateAmount('token1', value_0);
          }} balance={getTokenBalance(token1)} placeholder="0.0" chainId={connectedChainId as any} nativePrices={nativePrices} tokenPrices={tokenPrices} />
          </motion.div>
        </CardBody>
      </Card>

      {/* V3 Price Range (only for V3) */}
      <AnimatePresence>
        {version === 'v3' && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} style={{
        willChange: 'opacity, transform'
      }}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between w-full">
                  <h4 className="text-medium font-semibold">Price Range</h4>
                  <Switch size="sm" isSelected={fullRange} onValueChange={setFullRange}>
                    Full Range
                  </Switch>
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                {!fullRange && <>
                    <div className="grid grid-cols-2 gap-4">
                      <Input label="Min Price" placeholder="0.0" value={priceRange.lower.toString()} onValueChange={value_1 => setPriceRange(prev => ({
                  ...prev,
                  lower: parseFloat(value_1) || 0
                }))} endContent={<span className="text-xs text-default-500">
                            {token1?.symbol}/{token0?.symbol}
                          </span>} />
                      <Input label="Max Price" placeholder="0.0" value={priceRange.upper.toString()} onValueChange={value_2 => setPriceRange(prev_0 => ({
                  ...prev_0,
                  upper: parseFloat(value_2) || 0
                }))} endContent={<span className="text-xs text-default-500">
                            {token1?.symbol}/{token0?.symbol}
                          </span>} />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Current Price</span>
                        <span className="font-medium">
                          {formatNumber(priceRange.current, 6)} {token1?.symbol}/{token0?.symbol}
                        </span>
                      </div>
                  
                      {positionCalculation && 'inRange' in positionCalculation && <Alert color={positionCalculation.inRange ? 'success' : 'warning'} variant="flat" title={positionCalculation.inRange ? 'In Range' : 'Out of Range'} description={positionCalculation.inRange ? 'Your position will earn fees' : 'Your position will not earn fees until price moves into range'} />}
                    </div>
                  </>}

                {fullRange && <Alert color="primary" variant="flat" title="Full Range Position" description="Your liquidity will be active across all price ranges, similar to Uniswap V2." />}
              </CardBody>
            </Card>
          </motion.div>}
      </AnimatePresence>



      {/* Create New Pair Section - shown when pool doesn't exist */}
      {token0 && token1 && amount0 && amount1 && parseFloat(amount0) > 0 && parseFloat(amount1) > 0 && !poolInfo?.exists && <Card className="border-warning/50 border">
          <CardHeader className="pb-2">
            <h3 className="text-lg font-semibold text-warning">
              Create New {token0?.symbol} / {token1?.symbol} Pool
            </h3>
          </CardHeader>
          <CardBody className="pt-0 space-y-4">
            <p className="text-sm text-default-500">
              Creating a new pool on <strong>{dexInfo?.name || 'this DEX'}</strong>! 
              You are the first liquidity provider for this pair.
            </p>
            <p className="text-sm text-default-500">
              Starting price is determined by the ratio of {token0?.symbol} and {token1?.symbol} amounts:
            </p>
            <div className="bg-default-100 rounded-lg p-3 text-center">
              <span className="text-md font-semibold">
                1 {token0?.symbol} = {formatNumber(parseFloat(amount1) / parseFloat(amount0), 6)} {token1?.symbol}
              </span>
            </div>
            {computedPairAddress && <div className="pt-2 border-t border-default-200">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-default-400 text-center">Calculated Pair Address: {computedPairAddress}
                  </span>
                </div>
              </div>}
          </CardBody>
        </Card>}

      {/* Position Summary - shown when pool exists */}
      {positionCalculation && poolInfo?.exists && <Card>
            <CardHeader>
              <h4 className="text-medium font-semibold">Position Summary</h4>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-default-500">
                    {token0?.symbol} Amount
                  </span>
                  <span className="font-medium">
                    {formatNumber(parseFloat(amount0), 6)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-default-500">
                    {token1?.symbol} Amount
                  </span>
                  <span className="font-medium">
                    {formatNumber(parseFloat(amount1), 6)}
                  </span>
                </div>
                {version === 'v3' && 'inRange' in positionCalculation && <div className="flex justify-between">
                    <span className="text-sm text-default-500">Position Status</span>
                    <Chip size="sm" color={positionCalculation.inRange ? 'success' : 'warning'} variant="flat">
                      {positionCalculation.inRange ? 'In Range' : 'Out of Range'}
                    </Chip>
                  </div>}
              </div>
            </CardBody>
          </Card>}

      {/* Submit Button */}
      <Button color="secondary" size="lg" className="w-full text-shadow-[1px_2px_3px_rgb(0_0_0_/_0.79)]" isDisabled={!isFormValid} isLoading={state.isLoading} onPress={handleSubmit}>
        {state.isLoading ? <div className="flex items-center gap-2">
            <div className="rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>
              {state.step === 'approval' && 'Checking & Approving Tokens...'}
              {state.step === 'transaction' && 'Adding Liquidity...'}
              {state.step === 'confirmed' && 'Transaction Confirmed!'}
            </span>
          </div> : 'Add Liquidity'}
      </Button>

      {state.error && <Alert color="danger" variant="flat" title="Transaction Failed" description={state.error} />}

      {state.step === 'confirmed' && state.txHash && <Alert color="success" variant="flat" title="Liquidity Added Successfully!" description={<div className="space-y-2">
                <p>Your liquidity has been added to the pool.</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm">Transaction:</span>
                  <a href={`${chainSettings[connectedChainId as ChainType]?.blockExplorerUrl}/tx/${state.txHash}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-500 hover:text-blue-700 underline">
                    {state.txHash.slice(0, 6)}...{state.txHash.slice(-6)}
                  </a>
                </div>
              </div>} />}

      {/* Token Selection Modals */}
      <TokenSelect isOpen={isToken0SelectOpen} onOpenChange={onToken0SelectOpenChange} onChange={token_1 => setToken0(token_1)} label="Select Token A" tokens={Object.values(availableTokens)} account={address} chain={connectedChainId as ChainType} tokenData={mergedTokenData} />

      <TokenSelect isOpen={isToken1SelectOpen} onOpenChange={onToken1SelectOpenChange} onChange={token_2 => setToken1(token_2)} label="Select Token B" tokens={Object.values(availableTokens)} account={address} chain={connectedChainId as ChainType} tokenData={mergedTokenData} />

      {/* Existing Position Section */}
      {existingPosition && <ExistingPositionSection position={existingPosition} tokenData={tokenData} onRemoveClick={handleRemoveClick} />}

      {/* Remove Liquidity Modal */}
      {positionToRemove && <RemoveLiquidityModal position={positionToRemove} isOpen={isRemoveModalOpen} onOpenChange={onRemoveModalOpenChange} onSuccess={() => {
      onSuccess?.();
      onRemoveModalOpenChange();
    }} />}
    </div>;
}